"use client";

/**
 * LogoWall Component - Shared logo display component with multiple variants
 *
 * Usage Examples:
 *
 * // Hero2 style - 3 rows of 7 logos
 * <LogoWall variant="hero-rows" type="hero" rows={3} logosPerRow={7} />
 *
 * // Infinite scrolling (like Hero)
 * <LogoWall variant="infinite-scroll" type="startup" />
 *
 * // Small static for solution pages (4 logos)
 * <LogoWall variant="small-static" type="startup-small" />
 *
 * // Animated grid (like startup page)
 * <LogoWall variant="animated-grid" type="startup" />
 *
 * // Static grid with custom layout
 * <LogoWall variant="static-grid" type="enterprise" rows={2} logosPerRow={5} />
 */

import abridgeLogo from "@/app/(public)/shared/assets/logos/abridge.webp";
import agihouseLogo from "@/app/(public)/shared/assets/logos/agihouse.png";
import alphaExplorationLogo from "@/app/(public)/shared/assets/logos/alphaexplorationco.png";
import amexLogo from "@/app/(public)/shared/assets/logos/amex.png";
import blackRockLogo from "@/app/(public)/shared/assets/logos/blackrock.svg";
import camusLogo from "@/app/(public)/shared/assets/logos/camus.svg";
import chimeLogo from "@/app/(public)/shared/assets/logos/chime.png";
import cloroxLogo from "@/app/(public)/shared/assets/logos/clorox.png";
import clubhouseLogo from "@/app/(public)/shared/assets/logos/clubhouse.png";
import cubeLogo from "@/app/(public)/shared/assets/logos/cube.png";
import datadogLogo from "@/app/(public)/shared/assets/logos/datadog.png";
import enveritasLogo from "@/app/(public)/shared/assets/logos/enveritas.png";
import figmaLogo from "@/app/(public)/shared/assets/logos/figma.png";
import geniooLogo from "@/app/(public)/shared/assets/logos/genioo.png";
import gofundmeLogo from "@/app/(public)/shared/assets/logos/gofundme.svg";
import googleLogo from "@/app/(public)/shared/assets/logos/google.svg";
import greatQuestionLogo from "@/app/(public)/shared/assets/logos/great-question.svg";
import hubspotLogo from "@/app/(public)/shared/assets/logos/hubspot.png";
import imbueLogo from "@/app/(public)/shared/assets/logos/imbue.svg";
import mintlifyLogo from "@/app/(public)/shared/assets/logos/mintlify.svg";
import monteCarloLogo from "@/app/(public)/shared/assets/logos/montecarlo.png";
import motionLogo from "@/app/(public)/shared/assets/logos/motion.png";
import notionLogo from "@/app/(public)/shared/assets/logos/notion.png";
import nutrienLogo from "@/app/(public)/shared/assets/logos/nutrien.svg";
import offerfitLogo from "@/app/(public)/shared/assets/logos/offerfit.svg";
import onAndOffsiteLogo from "@/app/(public)/shared/assets/logos/onandoffsite.svg";
import posthogLogo from "@/app/(public)/shared/assets/logos/posthog.png";
import prodLogo from "@/app/(public)/shared/assets/logos/prod.svg";
import pylonLogo from "@/app/(public)/shared/assets/logos/pylon.svg";
import quantstampLogo from "@/app/(public)/shared/assets/logos/quantstamp.svg";
import renderLogo from "@/app/(public)/shared/assets/logos/render.png";
import royalCarribbeanLogo from "@/app/(public)/shared/assets/logos/royalcarribbean.svg";
import rutterLogo from "@/app/(public)/shared/assets/logos/rutter.svg";
import sbxiLogo from "@/app/(public)/shared/assets/logos/sbxi.svg";
import signalfireLogo from "@/app/(public)/shared/assets/logos/signalfire.svg";
import speakLogo from "@/app/(public)/shared/assets/logos/speak.svg";
import spruceLogo from "@/app/(public)/shared/assets/logos/spruce.png";
import stripeLogo from "@/app/(public)/shared/assets/logos/stripe.png";
import supabaseLogo from "@/app/(public)/shared/assets/logos/supabase.png";
// import windsurfLogo from "@/app/(public)/shared/assets/logos/windsurf.png";
import ycombinatorLogo from "@/app/(public)/shared/assets/logos/ycombinator.png";
import { motion } from "motion/react";
import React, { useEffect, useState } from "react";

// --- Logo data type ---
export interface Logo {
  id: string;
  type: string; // e.g. "startup", "enterprise", "general"
  name: string;
  src: string;
  width?: number;
  height?: number;
}

// --- Production Logo Datasets ---
export const productionLogos: Logo[] = [
  // === GENERAL/FEATURED LOGOS (Most recognizable) ===
  { id: "google", type: "general", name: "Google", src: googleLogo.src, width: 120, height: 40 },
  { id: "notion", type: "general", name: "Notion", src: notionLogo.src, width: 120, height: 40 },
  { id: "nutrien", type: "general", name: "Nutrien", src: nutrienLogo.src, width: 120, height: 40 },
  { id: "stripe", type: "general", name: "Stripe", src: stripeLogo.src, width: 120, height: 40 },
  { id: "supabase", type: "general", name: "Supabase", src: supabaseLogo.src, width: 120, height: 40 },
  { id: "gofundme", type: "general", name: "GoFundMe", src: gofundmeLogo.src, width: 120, height: 40 },

  // === ENTERPRISE LOGOS (Large companies) ===
  { id: "chime", type: "enterprise", name: "Chime", src: chimeLogo.src, width: 120, height: 40 },
  { id: "datadog", type: "enterprise", name: "Datadog", src: datadogLogo.src, width: 120, height: 40 },
  { id: "amex", type: "enterprise", name: "Amex", src: amexLogo.src, width: 120, height: 40 },
  { id: "blackrock", type: "enterprise", name: "BlackRock", src: blackRockLogo.src, width: 120, height: 40 },
  {
    id: "royalcarribbean",
    type: "enterprise",
    name: "Royal Caribbean",
    src: royalCarribbeanLogo.src,
    width: 120,
    height: 40,
  },
  { id: "render", type: "enterprise", name: "Render", src: renderLogo.src, width: 120, height: 40 },
  { id: "hubspot", type: "enterprise", name: "HubSpot", src: hubspotLogo.src, width: 120, height: 40 },
  { id: "clorox", type: "enterprise", name: "Clorox", src: cloroxLogo.src, width: 120, height: 40 },

  // === STARTUP LOGOS (YC companies, startups, VCs) ===
  { id: "pylon", type: "startup", name: "Pylon", src: pylonLogo.src, width: 120, height: 40 },
  { id: "signalfire", type: "startup", name: "Signalfire", src: signalfireLogo.src, width: 120, height: 40 },
  { id: "quantstamp", type: "startup", name: "Quantstamp", src: quantstampLogo.src, width: 120, height: 40 },
  { id: "camus", type: "startup", name: "Camus", src: camusLogo.src, width: 120, height: 40 },
  { id: "genioo", type: "startup", name: "Genioo", src: geniooLogo.src, width: 120, height: 40 },
  { id: "offerfit", type: "startup", name: "Offerfit", src: offerfitLogo.src, width: 120, height: 40 },
  { id: "ycombinator", type: "startup", name: "Y Combinator", src: ycombinatorLogo.src, width: 120, height: 40 },
  { id: "prod", type: "startup", name: "Prod", src: prodLogo.src, width: 120, height: 40 },
  { id: "mintlify", type: "startup", name: "Mintlify", src: mintlifyLogo.src, width: 120, height: 40 },
  { id: "imbue", type: "startup", name: "Imbue", src: imbueLogo.src, width: 120, height: 40 },
  { id: "abridge", type: "startup", name: "Abridge", src: abridgeLogo.src, width: 120, height: 40 },
  { id: "agihouse", type: "startup", name: "AGI House", src: agihouseLogo.src, width: 120, height: 40 },
  { id: "motion", type: "startup", name: "Motion", src: motionLogo.src, width: 120, height: 40 },
  {
    id: "alphaexploration",
    type: "startup",
    name: "Alpha Exploration",
    src: alphaExplorationLogo.src,
    width: 120,
    height: 40,
  },
  { id: "clubhouse", type: "startup", name: "Clubhouse", src: clubhouseLogo.src, width: 120, height: 40 },
  { id: "greatquestion", type: "startup", name: "Great Question", src: greatQuestionLogo.src, width: 120, height: 40 },
  { id: "rutter", type: "startup", name: "Rutter", src: rutterLogo.src, width: 120, height: 40 },
  { id: "sbxi", type: "startup", name: "SBXI", src: sbxiLogo.src, width: 120, height: 40 },
  { id: "cube", type: "startup", name: "Cube", src: cubeLogo.src, width: 120, height: 40 },
  // { id: "windsurf", type: "startup", name: "Windsurf", src: windsurfLogo.src, width: 120, height: 40 },
  { id: "montecarlo", type: "startup", name: "Monte Carlo", src: monteCarloLogo.src, width: 120, height: 40 },
  { id: "figma", type: "startup", name: "Figma", src: figmaLogo.src, width: 120, height: 40 },
  { id: "posthog", type: "startup", name: "PostHog", src: posthogLogo.src, width: 120, height: 40 },
  { id: "speak", type: "startup", name: "Speak", src: speakLogo.src, width: 120, height: 40 },

  // === FEATURED LOGOS (Mix of most recognizable from all categories) ===
  { id: "google-featured", type: "featured", name: "Google", src: googleLogo.src, width: 120, height: 40 },
  { id: "notion-featured", type: "featured", name: "Notion", src: notionLogo.src, width: 120, height: 40 },
  { id: "nutrien-featured", type: "featured", name: "Nutrien", src: nutrienLogo.src, width: 120, height: 40 },
  { id: "supabase-featured", type: "featured", name: "Supabase", src: supabaseLogo.src, width: 120, height: 40 },
  { id: "pylon-featured", type: "featured", name: "Pylon", src: pylonLogo.src, width: 120, height: 40 },
  { id: "stripe-featured", type: "featured", name: "Stripe", src: stripeLogo.src, width: 120, height: 40 },

  // === ADDITIONAL/MISC LOGOS ===
  { id: "enveritas", type: "general", name: "Enveritas", src: enveritasLogo.src, width: 120, height: 40 },
  { id: "onandoffsite", type: "general", name: "On and Offsite", src: onAndOffsiteLogo.src, width: 120, height: 40 },
  { id: "spruce", type: "general", name: "Spruce", src: spruceLogo.src, width: 120, height: 40 },
];

// --- Map from type to array of logo ids ---
export const logoTypeMap: Record<string, string[]> = {
  startup: [
    "pylon",
    "signalfire",
    "quantstamp",
    "camus",
    "genioo",
    "offerfit",
    "ycombinator",
    "prod",
    "mintlify",
    "imbue",
    "abridge",
    "agihouse",
    "alphaexploration",
    "clubhouse",
    "greatquestion",
    "rutter",
    "sbxi",
    "cube",
    // "windsurf",
    "montecarlo",
    "figma",
    "posthog",
    "speak",
    "motion",
  ],
  enterprise: ["chime", "datadog", "amex", "blackrock", "royalcarribbean", "render", "hubspot", "clorox"],
  general: ["google", "notion", "nutrien", "stripe", "supabase", "enveritas", "onandoffsite", "spruce", "gofundme"],
  featured: [
    "google-featured",
    "notion-featured",
    "nutrien-featured",
    "supabase-featured",
    "pylon-featured",
    "stripe-featured",
  ],
  // Curated sets for small-static variant (8 logos each)
  "startup-small": ["mintlify", "pylon", "supabase", "notion", "montecarlo", "speak", "figma", "posthog"],
  "enterprise-small": ["google", "amex", "blackrock", "nutrien", "clorox", "hubspot", "stripe", "chime"],
  "people-teams-small": ["montecarlo", "supabase", "notion", "cube", "stripe", "figma", "chime", "datadog"],
  hero: [
    // 28 most recognizable logos for landing page (4 rows × 7 logos)
    // Row 1
    "google",
    "nutrien",
    "blackrock",
    "amex",
    "hubspot",
    "notion",
    "stripe",
    // Row 2
    "clubhouse",
    "chime",
    "datadog",
    "supabase",
    "render",
    "gofundme",
    "pylon",
    // Row 3
    "posthog",
    "mintlify",
    "imbue",
    "figma",
    "signalfire",
    "camus",
    "offerfit",
    // Row 4
    "quantstamp",
    "speak",
    "prod",
    "abridge",
    "rutter",
    "motion",
    "cube",
  ],
};

// --- Map from id to logo object ---
export const logoIdMap: Record<string, Logo> = Object.fromEntries(productionLogos.map((logo) => [logo.id, logo]));

// --- Helper function to get logos by type ---
export const getLogosByType = (type: string): Logo[] => {
  if (logoTypeMap[type]) {
    return logoTypeMap[type].map((id) => logoIdMap[id]).filter(Boolean);
  }
  return productionLogos;
};

// --- Component Props ---
interface LogoWallProps {
  variant: "static-grid" | "infinite-scroll" | "animated-grid" | "small-static" | "hero-rows";
  type?: string; // e.g. "startup", "enterprise", "general", "featured"
  logos?: Logo[]; // Optional: override logos
  className?: string;
  // Grid-specific props
  rows?: number;
  logosPerRow?: number;
  // Trusted by text (for small-static variant)
  trustedByText?: string;
}

const LogoWall: React.FC<LogoWallProps> = ({
  variant,
  type = "general",
  logos,
  className = "",
  rows = 3,
  logosPerRow = 6,
  trustedByText,
}) => {
  // --- Select logos by type if type is provided ---
  let logosToShow: Logo[] = [];
  if (type && logoTypeMap[type]) {
    logosToShow = logoTypeMap[type].map((id) => logoIdMap[id]).filter(Boolean);
  } else if (logos && logos.length > 0) {
    logosToShow = logos;
  } else {
    logosToShow = productionLogos;
  }

  // --- Helper function for center-outward animation delay ---
  const getLogoDelay = (index: number, totalLogos: number) => {
    const center = (totalLogos - 1) / 2;
    const distanceFromCenter = Math.abs(index - center);
    return 0.9 + distanceFromCenter * 0.1;
  };

  // --- Static Grid Variant (like Hero2) ---
  if (variant === "static-grid") {
    const totalLogos = rows * logosPerRow;
    const logosForGrid: Logo[] = [];

    // Fill grid with logos, repeating if necessary
    for (let i = 0; i < totalLogos; i++) {
      logosForGrid.push(logosToShow[i % logosToShow.length]);
    }

    return (
      <div className={`flex flex-col justify-start items-center gap-16 ${className}`}>
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className="w-[1152px] inline-flex justify-between items-center">
            {logosForGrid.slice(rowIndex * logosPerRow, (rowIndex + 1) * logosPerRow).map((logo, index) => (
              <motion.div
                key={`${rowIndex}-${index}`}
                className="w-24 inline-flex flex-col justify-start items-start gap-2.5"
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.6,
                  delay: getLogoDelay(index, logosPerRow) + rowIndex * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <img
                  className="self-stretch w-auto object-contain transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "40px", maxWidth: "120px" }}
                  src={logo.src}
                  alt={logo.name}
                />
              </motion.div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // --- Infinite Scroll Variant (like Hero) ---
  if (variant === "infinite-scroll") {
    return (
      <div className={`w-full overflow-hidden ${className}`}>
        <motion.div
          className="flex items-center gap-12"
          animate={{
            x: [0, -1152], // Adjust based on logo count and spacing
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: 30,
              ease: "linear",
            },
          }}
          style={{ width: "3456px" }} // 3 sets for smooth looping
        >
          {/* First set of logos */}
          {logosToShow.map((logo, index) => (
            <div key={`first-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
              <img
                src={logo.src}
                alt={logo.name}
                className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                style={{ height: "32px", maxWidth: "96px" }}
              />
            </div>
          ))}

          {/* Second set for seamless loop */}
          {logosToShow.map((logo, index) => (
            <div key={`second-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
              <img
                src={logo.src}
                alt={logo.name}
                className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                style={{ height: "32px", maxWidth: "96px" }}
              />
            </div>
          ))}

          {/* Third set for extra smooth looping */}
          {logosToShow.map((logo, index) => (
            <div key={`third-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
              <img
                src={logo.src}
                alt={logo.name}
                className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                style={{ height: "32px", maxWidth: "96px" }}
              />
            </div>
          ))}
        </motion.div>
      </div>
    );
  }

  // --- Small Static Variant (for solution pages) ---
  if (variant === "small-static") {
    // Support both 4 and 8 logos with responsive layout
    const maxLogos = logosToShow.length <= 4 ? 4 : 8;
    const displayLogos = logosToShow.slice(0, maxLogos);
    const isEightLogos = displayLogos.length > 4;

    return (
      <div className={`flex flex-col gap-6 ${className}`}>
        {trustedByText && <p className="text-body text-landing-secondary md:flex hidden">{trustedByText}</p>}
        {isEightLogos ? (
          // 8 logos: Desktop/Tablet: 4 columns (2x4), Mobile: 2 columns (4x2)
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 md:gap-x-12 gap-y-6 md:gap-y-8 justify-start">
            {displayLogos.map((logo, index) => (
              <motion.div
                key={logo.id}
                className="flex items-center justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="w-auto object-contain opacity-70 hover:opacity-90 transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "32px", maxWidth: "100px" }}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          // 4 logos: Desktop/Tablet: single row, Mobile: 2x2 grid
          <div className="grid grid-cols-2 md:flex md:items-center md:justify-start gap-8 md:gap-12">
            {displayLogos.map((logo, index) => (
              <motion.div
                key={logo.id}
                className="flex items-center justify-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="w-auto object-contain opacity-70 hover:opacity-90 transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "32px", maxWidth: "100px" }}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // --- Animated Grid Variant (like startup LogoWall) ---
  if (variant === "animated-grid") {
    return (
      <div className={`w-full grid grid-cols-5 grid-rows-2 gap-2 ${className}`}>
        {logosToShow.slice(0, 10).map((logo, index) => (
          <motion.div
            key={logo.id}
            className="h-48 relative rounded-xl overflow-hidden transition-all duration-500 ease-in-out cursor-pointer flex items-center justify-center p-6"
            style={{ boxShadow: "inset 0 0 0 1px var(--landing-border-primary)" }}
            initial={{ opacity: 0, filter: "blur(10px)", scale: 0.9 }}
            animate={{ opacity: 1, filter: "blur(0px)", scale: 1 }}
            transition={{ duration: 0.3, ease: "easeInOut", delay: index * 0.1 }}
          >
            <img
              src={logo.src}
              alt={logo.name}
              className="max-w-full max-h-full object-contain filter opacity-90 hover:opacity-80 transition-opacity duration-300"
            />
          </motion.div>
        ))}
      </div>
    );
  }

  // --- Hero Rows Variant (current Hero2 style) ---
  if (variant === "hero-rows") {
    const [shouldGrayscale, setShouldGrayscale] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
      const timer = setTimeout(() => {
        setShouldGrayscale(true);
      }, 1500);
      return () => clearTimeout(timer);
    }, []);

    // Always render 4 rows for hero-rows, but hide 4th row on smaller screens
    const heroRows = 4;

    return (
      <>
        {/* Desktop: Rows Layout */}
        <div
          className={`hidden lg:flex flex-col justify-start items-center gap-12 ${className}`}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {Array.from({ length: heroRows }, (_, rowIndex) => {
            const startIndex = rowIndex * logosPerRow;
            const rowLogos = [];
            for (let i = 0; i < logosPerRow; i++) {
              rowLogos.push(logosToShow[(startIndex + i) % logosToShow.length]);
            }

            return (
              <div
                key={rowIndex}
                className={`w-[1152px] inline-flex justify-between items-center ${
                  rowIndex === 3 ? "hidden xl:flex" : ""
                }`}
              >
                {rowLogos.map((logo, index) => (
                  <motion.div
                    key={`${rowIndex}-${index}`}
                    className="w-24 inline-flex flex-col justify-start items-start gap-2.5"
                    initial={{ opacity: 0, y: 20, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.6,
                      delay: getLogoDelay(index, logosPerRow) + rowIndex * 0.1,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  >
                    <motion.img
                      className="self-stretch w-auto object-contain"
                      style={{ height: "32px", maxWidth: "96px" }}
                      src={logo.src}
                      alt={logo.name}
                      initial={{ filter: "grayscale(0%)" }}
                      animate={{
                        filter: isHovering || !shouldGrayscale ? "grayscale(0%)" : "grayscale(100%)",
                      }}
                      transition={{
                        duration: 0.3,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                    />
                  </motion.div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Tablet and Mobile: Horizontal Scrolling */}
        <div className={`lg:hidden w-full overflow-hidden ${className}`}>
          <motion.div
            className="flex items-center gap-8"
            animate={{
              x: [0, -1152],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: "loop",
                duration: 30,
                ease: "linear",
              },
            }}
            style={{ width: "3456px" }}
          >
            {/* First set of logos */}
            {logosToShow.map((logo, index) => (
              <div key={`first-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "28px", maxWidth: "84px" }}
                />
              </div>
            ))}

            {/* Second set for smooth looping */}
            {logosToShow.map((logo, index) => (
              <div key={`second-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "28px", maxWidth: "84px" }}
                />
              </div>
            ))}

            {/* Third set for extra smooth looping */}
            {logosToShow.map((logo, index) => (
              <div key={`third-${index}`} className="shrink-0 flex items-center justify-center w-32 h-12">
                <img
                  src={logo.src}
                  alt={logo.name}
                  className="w-auto object-contain opacity-60 hover:opacity-80 transition-all duration-300 filter grayscale hover:grayscale-0"
                  style={{ height: "28px", maxWidth: "84px" }}
                />
              </div>
            ))}
          </motion.div>
        </div>
      </>
    );
  }

  // Default fallback
  return null;
};

export default LogoWall;
